import Billboard from 'components/Billboard'
import LookerEmbedTab from 'components/LookerEmbedTab'
import OloWebhookBanner from 'components/OloWebhookBanner'
import { buildTranslate } from 'locales'
import React from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'react-router-redux'
import { useAbility } from 'utilities/ability'
import type { OloWebhookPanel } from 'utilities/oloWebhookStatus'
import { ACTIVE_LOCATIONS_SETTINGS_PATH } from '../routes'
import NoGuestFacingLocations from './NoGuestFacingLocations'

// The default "Overview" tab (AI-43 reversal): the RecoveryAI performance
// dashboard is no longer built in the FE. It embeds a Looker dashboard so the
// data team owns the reported content independently of this app. LookerEmbedTab
// owns the heading, iframe, and footer chrome.
//
// AI-241: which dashboard renders depends on how widely RecoveryAI is deployed —
// the `pilot` dashboard while it runs at a subset of locations, the `full`
// dashboard once it covers every guest-facing location (`none` when it covers
// none). Landing derives the stage from guest-facing location coverage (no
// persisted field) and passes it in; only ONE dashboard
// ever renders at a time. Both are wired here (Bailey 2026-07-07: both required
// by launch) and default to `pilot`, the conservative choice while the stage is
// still unknown.
//
// O1 (cross-team — gates the LIVE render only): each path is a relative
// `target_path` LookerIframe sends to `/looker/embed_url` (not a full
// looker.com URL). It renders only once the backend allowlists that path on the
// `looker-embed-paths` LaunchDarkly flag (user key `looker-dashboards-551` /
// `looker-dashboards-552`). Until then LookerIframe renders the graceful
// <LookerError/> permission state — that is EXPECTED, not a bug.
export const RECOVERY_AI_LOOKER_PATHS = {
  pilot: 'dashboards/551',
  full: 'dashboards/552',
} as const

// `none`: RecoveryAI is on at no guest-facing store, so there is no data to
// report — Overview shows a one-line empty state under the Turn-on billboard
// instead of a dashboard.
export type DeploymentStage = keyof typeof RECOVERY_AI_LOOKER_PATHS | 'none'

type Props = {
  deploymentStage?: DeploymentStage
  remainingLocations?: number
  /** AI-1295 (FR-39) design revision: reuses the Live updates OloWebhookBanner
   * unchanged. Landing computes this (isActive/isOloOrdering gating lives
   * there) and withholds it entirely rather than passing a "no panel" state,
   * so omission IS the "don't render" signal. */
  oloWebhook?: OloWebhookPanel
}

// FR-01 partial-state chrome (AI-241): the "Turn on RecoveryAI at more
// locations" billboard sits ABOVE the dashboard (or the `none` empty state) only
// when coverage is partial. Gate on `remainingLocations > 0` (derived once in
// Landing from the same guest-facing counts as `deploymentStage`) so full
// coverage (0) and loading (undefined) both render no billboard.
// The Billboard is presentational now (promoted to components/Billboard); this
// scene owns its eyebrow/CTA copy and its navigation. Overview deep-links the CTA
// to Settings → Active Locations; the hash is the InnerTabs sub-tab key.
const t = buildTranslate('recovery_ai.overview')

const Overview = ({
  deploymentStage = 'pilot',
  remainingLocations,
  oloWebhook,
}: Props) => {
  const dispatch = useDispatch()
  // RBAC: a view-only ('access', not 'manage') RecoveryAI operator can reach
  // this tab but must not be able to fire the CTA's navigation into the
  // edit-only Active Locations settings pane. Mirrors the AI-64 gate the
  // Settings panes already apply (EscalationRulesPane / DailyBudgetPane).
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')

  return (
    <>
      {oloWebhook && (
        <div className="mb-l">
          <OloWebhookBanner
            connectionState={oloWebhook.connectionState}
            lastReceivedAt={oloWebhook.lastReceivedAt}
            needsReconnect={oloWebhook.needsReconnect}
            onManage={oloWebhook.onManage}
          />
        </div>
      )}
      {remainingLocations != null && remainingLocations > 0 && (
        <Billboard
          eyebrow={t('billboard.eyebrow', { count: remainingLocations })}
          ctaLabel={t('billboard.cta')}
          onCtaClick={() => dispatch(push(ACTIVE_LOCATIONS_SETTINGS_PATH))}
          disabled={!canManage}
        />
      )}
      {deploymentStage === 'none' ? (
        <NoGuestFacingLocations />
      ) : (
        <LookerEmbedTab
          path={RECOVERY_AI_LOOKER_PATHS[deploymentStage]}
          translationKey="recovery_ai.overview.looker"
        />
      )}
    </>
  )
}

export default Overview
